import { N, round, ceil } from './helpers.mjs';

// Gardeners & landscapers calculators. Practical UK estimating defaults; figures are guidance.
export const GARDENERS = [
  {
    slug: 'plant-spacing',
    trade: 'gardeners',
    name: 'Plant Spacing Calculator',
    blurb: 'Works out how many plants you need for a bed at a given spacing. Square grid + triangular (offset) planting which packs ~15% more in.',
    inputs: [
      { key: 'length', label: 'Bed length', unit: 'm', placeholder: '5' },
      { key: 'width', label: 'Bed width', unit: 'm', placeholder: '2' },
      { key: 'spacing', label: 'Plant spacing', unit: 'cm', placeholder: '30', default: 30 },
    ],
    compute: (v) => {
      const area = N(v.length) * N(v.width);
      const s = Math.max(0.01, N(v.spacing) / 100);
      const square = ceil(area / (s * s));
      const triangular = ceil(square * 1.155);
      return [
        { label: 'Bed area', value: round(area, 2), unit: 'm²' },
        { label: 'Plants (square grid)', value: square, unit: 'plants' },
        { label: 'Plants (triangular/offset)', value: triangular, unit: 'plants' },
      ];
    },
  },
  {
    slug: 'lawn-seed',
    trade: 'gardeners',
    name: 'Lawn Seed Calculator',
    blurb: 'Grass seed quantity for a new lawn (35 g/m²), overseeding (25 g/m²) or patch repair (50 g/m²).',
    inputs: [
      { key: 'area', label: 'Lawn area', unit: 'm²', placeholder: '50' },
      { key: 'job', label: 'Job type', type: 'select', default: 'new',
        options: [
          { value: 'new', label: 'New lawn (35 g/m²)' },
          { value: 'overseed', label: 'Overseeding (25 g/m²)' },
          { value: 'repair', label: 'Patch repair (50 g/m²)' },
        ] },
      { key: 'bagKg', label: 'Bag size', unit: 'kg', placeholder: '20', default: 20 },
    ],
    compute: (v) => {
      const rate = { new: 35, overseed: 25, repair: 50 }[v.job || 'new'];
      const grams = N(v.area) * rate;
      const kg = grams / 1000;
      const bagKg = Math.max(0.1, N(v.bagKg) || 20);
      return [
        { label: `Application rate`, value: rate, unit: 'g/m²' },
        { label: 'Seed required', value: round(kg, 2), unit: 'kg' },
        { label: `Bags (${bagKg}kg)`, value: ceil(kg / bagKg), unit: 'bags' },
      ];
    },
  },
  {
    slug: 'compost',
    trade: 'gardeners',
    name: 'Compost Calculator',
    blurb: 'Compost / topsoil volume for beds & borders, raised beds or planters. Outputs m³, litres, 50 L bags and bulk bags (≈ 0.6 m³).',
    inputs: [
      { key: 'length', label: 'Length / diameter', unit: 'm', placeholder: '3' },
      { key: 'width', label: 'Width (0 for round planter)', unit: 'm', placeholder: '1', default: 0 },
      { key: 'depth', label: 'Depth to fill', unit: 'cm', placeholder: '15', default: 15 },
      { key: 'shape', label: 'Shape', type: 'select', default: 'rect',
        options: [
          { value: 'rect', label: 'Rectangular bed / raised bed' },
          { value: 'round', label: 'Round planter (use diameter)' },
        ] },
    ],
    compute: (v) => {
      const depth = N(v.depth) / 100;
      let area;
      if (v.shape === 'round') { const r = N(v.length) / 2; area = Math.PI * r * r; }
      else area = N(v.length) * N(v.width);
      const m3 = area * depth;
      const litres = m3 * 1000;
      return [
        { label: 'Volume', value: round(m3, 3), unit: 'm³' },
        { label: 'Volume', value: round(litres, 0), unit: 'litres' },
        { label: '50 L bags', value: ceil(litres / 50), unit: 'bags' },
        { label: 'Bulk bags (0.6 m³)', value: ceil(m3 / 0.6), unit: 'bulk bags' },
      ];
    },
  },
  {
    slug: 'mulch-bark',
    trade: 'gardeners',
    name: 'Mulch / Bark Calculator',
    blurb: 'Bark or mulch by coverage and depth. Outputs m³, litres, 70 L bags and bulk bags (≈ 0.75 m³).',
    inputs: [
      { key: 'area', label: 'Area to cover', unit: 'm²', placeholder: '20' },
      { key: 'depth', label: 'Mulch depth', unit: 'cm', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const m3 = N(v.area) * (N(v.depth) / 100);
      const litres = m3 * 1000;
      return [
        { label: 'Volume', value: round(m3, 3), unit: 'm³' },
        { label: 'Volume', value: round(litres, 0), unit: 'litres' },
        { label: '70 L bags', value: ceil(litres / 70), unit: 'bags' },
        { label: 'Bulk bags (0.75 m³)', value: ceil(m3 / 0.75), unit: 'bulk bags' },
      ];
    },
  },
  {
    slug: 'drip-pipe',
    trade: 'gardeners',
    name: 'Drip Pipe Calculator',
    blurb: 'Drip irrigation pipe length and emitters for a bed laid in rows.',
    inputs: [
      { key: 'length', label: 'Bed length (run direction)', unit: 'm', placeholder: '6' },
      { key: 'width', label: 'Bed width', unit: 'm', placeholder: '2' },
      { key: 'rowSpacing', label: 'Row spacing', unit: 'm', placeholder: '0.3', default: 0.3 },
      { key: 'emitterSpacing', label: 'Emitter spacing', unit: 'm', placeholder: '0.3', default: 0.3 },
    ],
    compute: (v) => {
      const rows = N(v.width) > 0 ? ceil(N(v.width) / Math.max(0.05, N(v.rowSpacing))) : 0;
      const pipe = round(rows * N(v.length), 1);
      const emitters = ceil(pipe / Math.max(0.05, N(v.emitterSpacing)));
      return [
        { label: 'Rows', value: rows, unit: '' },
        { label: 'Drip pipe required', value: pipe, unit: 'm' },
        { label: 'Emitters', value: emitters, unit: '' },
      ];
    },
  },
  {
    slug: 'sprinkler-coverage',
    trade: 'gardeners',
    name: 'Sprinkler Coverage Calculator',
    blurb: 'How many sprinklers to cover an area, allowing for head-to-head overlap (~40% effective coverage per head).',
    inputs: [
      { key: 'area', label: 'Area to water', unit: 'm²', placeholder: '100' },
      { key: 'radius', label: 'Sprinkler throw radius', unit: 'm', placeholder: '4', default: 4 },
    ],
    compute: (v) => {
      const r = Math.max(0.1, N(v.radius));
      const full = Math.PI * r * r;
      const effective = full * 0.4; // head-to-head overlap
      const heads = N(v.area) > 0 ? ceil(N(v.area) / effective) : 0;
      return [
        { label: 'Full circle per head', value: round(full, 1), unit: 'm²' },
        { label: 'Effective coverage (overlap)', value: round(effective, 1), unit: 'm²' },
        { label: 'Sprinkler heads', value: heads, unit: 'heads' },
      ];
    },
  },
  {
    slug: 'water-requirement',
    trade: 'gardeners',
    name: 'Water Requirement Calculator',
    blurb: 'Weekly watering volume for a lawn/bed. 1 mm of water = 1 litre per m². Default 25 mm/week.',
    inputs: [
      { key: 'area', label: 'Area', unit: 'm²', placeholder: '100' },
      { key: 'mm', label: 'Water per week', unit: 'mm', placeholder: '25', default: 25 },
    ],
    compute: (v) => {
      const perWeek = N(v.area) * N(v.mm);
      return [
        { label: 'Water per week', value: round(perWeek, 0), unit: 'litres' },
        { label: 'Water per week', value: round(perWeek / 1000, 2), unit: 'm³' },
        { label: 'Water per day', value: round(perWeek / 7, 0), unit: 'litres' },
      ];
    },
  },
  {
    slug: 'lawn-treatment',
    trade: 'gardeners',
    name: 'Lawn Treatment Calculator',
    blurb: 'Product quantity for feed, moss killer, weed killer or combined feed-weed-moss by area.',
    inputs: [
      { key: 'area', label: 'Lawn area', unit: 'm²', placeholder: '100' },
      { key: 'type', label: 'Treatment', type: 'select', default: 'feed',
        options: [
          { value: 'feed', label: 'Lawn feed (35 g/m²)' },
          { value: 'moss', label: 'Moss killer / sulphate of iron (35 g/m²)' },
          { value: 'weed', label: 'Weed & feed (20 g/m²)' },
          { value: 'combo', label: 'Feed, weed & moss (4-in-1) (35 g/m²)' },
        ] },
      { key: 'bagKg', label: 'Bag size', unit: 'kg', placeholder: '20', default: 20 },
    ],
    compute: (v) => {
      const rate = { feed: 35, moss: 35, weed: 20, combo: 35 }[v.type || 'feed'];
      const kg = (N(v.area) * rate) / 1000;
      const bagKg = Math.max(0.1, N(v.bagKg) || 20);
      return [
        { label: 'Application rate', value: rate, unit: 'g/m²' },
        { label: 'Product required', value: round(kg, 2), unit: 'kg' },
        { label: `Bags (${bagKg}kg)`, value: ceil(kg / bagKg), unit: 'bags' },
      ];
    },
  },
  {
    slug: 'fertiliser',
    trade: 'gardeners',
    name: 'Fertiliser Calculator',
    blurb: 'Granular fertiliser quantity from area and your chosen application rate.',
    inputs: [
      { key: 'area', label: 'Area', unit: 'm²', placeholder: '100' },
      { key: 'rate', label: 'Application rate', unit: 'g/m²', placeholder: '35', default: 35 },
      { key: 'bagKg', label: 'Bag size', unit: 'kg', placeholder: '25', default: 25 },
    ],
    compute: (v) => {
      const grams = N(v.area) * N(v.rate);
      const kg = grams / 1000;
      const bagKg = Math.max(0.1, N(v.bagKg) || 25);
      return [
        { label: 'Fertiliser required', value: round(grams, 0), unit: 'g' },
        { label: 'Fertiliser required', value: round(kg, 2), unit: 'kg' },
        { label: `Bags (${bagKg}kg)`, value: ceil(kg / bagKg), unit: 'bags' },
      ];
    },
  },
  {
    slug: 'spray-mix',
    trade: 'gardeners',
    name: 'Spray Mix Calculator',
    blurb: 'Knapsack / spray mix — total water and concentrate from area, water rate and product dilution.',
    inputs: [
      { key: 'area', label: 'Area to spray', unit: 'm²', placeholder: '200' },
      { key: 'waterRate', label: 'Water rate', unit: 'L/m²', placeholder: '0.1', default: 0.1 },
      { key: 'dilution', label: 'Product dilution', unit: 'mL per L water', placeholder: '50', default: 50 },
    ],
    compute: (v) => {
      const water = N(v.area) * N(v.waterRate);
      const concentrate = water * N(v.dilution);
      return [
        { label: 'Total water', value: round(water, 1), unit: 'litres' },
        { label: 'Total concentrate', value: round(concentrate, 0), unit: 'mL' },
        { label: 'Total concentrate', value: round(concentrate / 1000, 2), unit: 'litres' },
      ];
    },
  },
  {
    slug: 'lawn-mowing-pricing',
    trade: 'gardeners',
    name: 'Lawn Mowing Pricing Calculator',
    blurb: 'Suggested price for a mow from area and your rate, with a minimum call-out charge and a rough time estimate.',
    inputs: [
      { key: 'area', label: 'Lawn area', unit: 'm²', placeholder: '200' },
      { key: 'rate', label: 'Your rate', unit: '£/m²', placeholder: '0.04', default: 0.04 },
      { key: 'minCharge', label: 'Minimum charge', unit: '£', placeholder: '20', default: 20 },
      { key: 'speed', label: 'Mowing speed', unit: 'm²/min', placeholder: '50', default: 50 },
    ],
    compute: (v) => {
      const raw = N(v.area) * N(v.rate);
      const price = Math.max(N(v.minCharge), raw);
      const mins = N(v.speed) > 0 ? N(v.area) / N(v.speed) : 0;
      return [
        { label: 'Calculated price', value: round(raw, 2), unit: '£' },
        { label: 'Price to charge (min applied)', value: round(price, 2), unit: '£' },
        { label: 'Estimated mowing time', value: round(mins, 0), unit: 'mins' },
      ];
    },
  },
  {
    slug: 'garden-clearance',
    trade: 'gardeners',
    name: 'Garden Clearance Estimator',
    blurb: 'Estimates green-waste volume, labour days and disposal for a clearance by area and overgrowth level.',
    inputs: [
      { key: 'area', label: 'Garden area', unit: 'm²', placeholder: '80' },
      { key: 'level', label: 'Overgrowth', type: 'select', default: 'medium',
        options: [
          { value: 'light', label: 'Light (0.05 m³/m²)' },
          { value: 'medium', label: 'Medium (0.1 m³/m²)' },
          { value: 'heavy', label: 'Heavy / jungle (0.2 m³/m²)' },
        ] },
      { key: 'dayRate', label: 'Labour day rate', unit: '£', placeholder: '200', default: 200 },
      { key: 'm2PerDay', label: 'Clearance rate', unit: 'm²/day', placeholder: '40', default: 40 },
    ],
    compute: (v) => {
      const factor = { light: 0.05, medium: 0.1, heavy: 0.2 }[v.level || 'medium'];
      const m3 = N(v.area) * factor;
      const days = N(v.m2PerDay) > 0 ? N(v.area) / N(v.m2PerDay) : 0;
      const labour = days * N(v.dayRate);
      return [
        { label: 'Green waste volume', value: round(m3, 2), unit: 'm³' },
        { label: 'Bulk bags (0.75 m³)', value: ceil(m3 / 0.75), unit: 'bags' },
        { label: 'Estimated labour', value: round(days, 1), unit: 'days' },
        { label: 'Labour cost', value: round(labour, 2), unit: '£' },
      ];
    },
  },
  {
    slug: 'green-waste',
    trade: 'gardeners',
    name: 'Green Waste Calculator',
    blurb: 'Volume of a green-waste pile and how many bulk bags / van loads it makes.',
    inputs: [
      { key: 'length', label: 'Pile length', unit: 'm', placeholder: '2' },
      { key: 'width', label: 'Pile width', unit: 'm', placeholder: '1.5' },
      { key: 'height', label: 'Pile height', unit: 'm', placeholder: '1', default: 1 },
      { key: 'vanM3', label: 'Van / trailer capacity', unit: 'm³', placeholder: '3', default: 3 },
    ],
    compute: (v) => {
      const m3 = N(v.length) * N(v.width) * N(v.height);
      const vanM3 = Math.max(0.1, N(v.vanM3) || 3);
      return [
        { label: 'Volume', value: round(m3, 2), unit: 'm³' },
        { label: 'Bulk bags (0.75 m³)', value: ceil(m3 / 0.75), unit: 'bags' },
        { label: 'Van / trailer loads', value: ceil(m3 / vanM3), unit: 'loads' },
      ];
    },
  },
];
